import { useCallback, useContext, useMemo } from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import _ from 'lodash';
import { IntlContext, TranslatorContext } from '@jutro/locale';
import CommonConstants from 'sg-common-constants';
import SgIpidDetails from 'sg-ipid-details';
import { SgDateUtil, SgSubmissionUtil, SgIpidUtil } from 'saga-modules';
import SphConstants from 'sg-sph-constants';
import stringUtil from 'gw-portals-util-js/StringUtil';
import metadata from './SgSphIpid.metadata.json5';
import messages from './SgSphIpid.messages';
import IpidConfig from './SgSphIpidConfig.json';

function SgSphIpid(props) {
    const {
        model
    } = props;

    const { coverages } = model.lobData.privateHome.offerings[0];
    const { productType } = coverages;
    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);
    const allCoverages = useMemo(() => {
        return _.concat(coverages.addOnsCoverages, coverages.basicCoverages, coverages.contentsExtensionCoverages);
    }, [coverages.addOnsCoverages, coverages.basicCoverages, coverages.contentsExtensionCoverages]);

    const coverLevelMessages = useMemo(() => {
        return {
            commonCoveredCountries: ['CommonCoveredCountries1', 'CommonCoveredCountries2', 'CommonCoveredCountries3'],
            commonObligations: ['SphObigations1', 'SphObigations2', 'SphObigations3', 'SphObigations4', 'SphObigations5'],
            sheObligations: ['SheObligations1', 'SheObligations2', 'SheObligations3'],
            legalObligations: ['LegalExpansesObligations1', 'LegalExpansesObligations2', 'LegalExpansesObligations3', 'LegalExpansesObligations4']
        };
    }, []);

    const getCoverages = useMemo(() => {
        return {
            buildingCover: SgSubmissionUtil.getCoverageByCode(allCoverages, SphConstants.COVERAGES_CODES.Building),
            contentsCover: SgSubmissionUtil.getCoverageByCode(allCoverages, SphConstants.COVERAGES_CODES.Contents),
            belongingsAwayCover: SgSubmissionUtil.getCoverageByCode(allCoverages, SphConstants.COVERAGES_CODES.PersonalBelongings),
            specifiedItemsCoverAnywhere: SgSubmissionUtil.getCoverageByCode(allCoverages, SphConstants.COVERAGES_CODES.SpecifiedCoverAnywhere),
            rentGuaranteeCover: SgSubmissionUtil.getCoverageByCode(allCoverages, SphConstants.COVERAGES_CODES.RentGuarantee),
            legalCover: SgSubmissionUtil.getCoverageByCode(allCoverages, SphConstants.COVERAGES_CODES.LegalExpenses),
            sheEmergencyCover: SgSubmissionUtil.getCoverageByCode(allCoverages, SphConstants.COVERAGES_CODES.HomeEmergency),
            boilerCover: SgSubmissionUtil.getCoverageByCode(allCoverages, SphConstants.COVERAGES_CODES.BoilerService),
            isSagaPlus: coverages.isSagaPlus
        };
    }, [allCoverages, coverages.isSagaPlus]);

    const isRenewal = useMemo(() => {
        const { jobType } = model.baseData;
        return jobType && jobType === CommonConstants.JOB_TYPES.RENEWAL;
    }, [model]);

    const hasSpecifiedEndorsement = useCallback((code) => {
        if (isRenewal) {
            return model.lobData.privateHome.offerings[0].specificEndorsements.includes(code);
        }
        return false;
    }, [isRenewal, model]);

    const getAllCoverLevels = useMemo(() => {
        const coverLevels = {};
        coverLevels.buildingsCover = getCoverages.buildingCover.selected;
        coverLevels.contentsCover = getCoverages.contentsCover.selected;

        if (getCoverages.buildingCover) {
            const buildingCoverLevel = SgSubmissionUtil.getTermByCode(getCoverages.buildingCover,
                SphConstants.COVERAGES_CODES.BuildingLevelTermName).chosenTerm;
            switch (buildingCoverLevel) {
                case `${SphConstants.COVERAGES_CODES.BuildingLevelTermName}_${SphConstants.COVERAGES_CODES.CoverLevelPlus}`:
                    coverLevels.buildingsPlus = true;
                    break;
                case `${SphConstants.COVERAGES_CODES.BuildingLevelTermName}_${SphConstants.COVERAGES_CODES.CoverLevelSelectAd}`:
                    coverLevels.buildingsAd = true;
                    break;
                case `${SphConstants.COVERAGES_CODES.BuildingLevelTermName}_${SphConstants.COVERAGES_CODES.CoverLevelSelect}`:
                    coverLevels.buildingsSelect = true;
                    break;
                default:
            }
        }
        if (getCoverages.contentsCover) {
            const contentsCoverLevel = SgSubmissionUtil.getTermByCode(getCoverages.contentsCover,
                SphConstants.COVERAGES_CODES.ContentsLevelTermName).chosenTerm;
            switch (contentsCoverLevel) {
                case `${SphConstants.COVERAGES_CODES.ContentsLevelTermName}_${SphConstants.COVERAGES_CODES.CoverLevelPlus}`:
                    coverLevels.contentsPlus = true;
                    break;
                case `${SphConstants.COVERAGES_CODES.ContentsLevelTermName}_${SphConstants.COVERAGES_CODES.CoverLevelSelectAd}`:
                    coverLevels.contentsAd = true;
                    break;
                case `${SphConstants.COVERAGES_CODES.ContentsLevelTermName}_${SphConstants.COVERAGES_CODES.CoverLevelSelect}`:
                    coverLevels.contentsSelect = true;
                    break;
                default:
            }
        }
        const hasEndorsement850 = hasSpecifiedEndorsement('850');
        const hasEndorsement851 = hasSpecifiedEndorsement('851');
        const hasEndorsement852 = hasSpecifiedEndorsement('852');
        coverLevels.legalCover = getCoverages.legalCover && getCoverages.legalCover.selected;
        coverLevels.homeEmergencyCover = getCoverages.sheEmergencyCover && getCoverages.sheEmergencyCover.selected;
        coverLevels.boilerCover = (getCoverages.boilerCover && getCoverages.boilerCover.selected) || hasEndorsement850;
        coverLevels.homeHeatingEmergencyCover = (getCoverages.sheEmergencyCover && getCoverages.sheEmergencyCover.terms
            .find((el) => el.patternCode === SphConstants.COVERAGES_CODES.HomeEmergencyTermName)
            .chosenTerm === SphConstants.COVERAGES_CODES.HomeEmergencyHeatingTermOptionCode) || hasEndorsement850;
        coverLevels.completeHomeAndHeatingCare = hasEndorsement850;
        coverLevels.addonBoilerCover = getCoverages.boilerCover && getCoverages.boilerCover.selected && !hasEndorsement850;
        coverLevels.addonHomeHeatingEmergencyCover = getCoverages.sheEmergencyCover && getCoverages.sheEmergencyCover.terms
            .find((el) => el.patternCode === SphConstants.COVERAGES_CODES.HomeEmergencyTermName)
            .chosenTerm === SphConstants.COVERAGES_CODES.HomeEmergencyHeatingTermOptionCode && !hasEndorsement850;
        coverLevels.accidentalDamageEndo851 = coverLevels.buildingsSelect && hasEndorsement851;
        coverLevels.undergroundPipes = coverLevels.buildingsSelect && !hasEndorsement851;
        coverLevels.accidentalDamageEndo852 = coverLevels.contentsSelect && hasEndorsement852;
        coverLevels.unSpecifiedItemCover = getCoverages.belongingsAwayCover && getCoverages.belongingsAwayCover.selected;
        coverLevels.specifiedItemsCoverAnywhere = getCoverages.specifiedItemsCoverAnywhere && getCoverages.specifiedItemsCoverAnywhere.selected;
        coverLevels.noSpecifiedItemCover = !coverLevels.unSpecifiedItemCover && !coverLevels.specifiedItemsCoverAnywhere;
        coverLevels.isHomeEmergency = coverLevels.homeEmergencyCover && !coverLevels.homeHeatingEmergencyCover;
        coverLevels.isHomeAndHeatingEmergency = coverLevels.homeEmergencyCover && coverLevels.homeHeatingEmergencyCover;
        return coverLevels;
    }, [getCoverages, hasSpecifiedEndorsement]);

    const getCurrencyValue = useCallback((value) => {
        return intl.formatNumber(
            value.amount ? value.amount : value,
            {
                style: 'currency',
                currency: value.currency ? value.currency : 'gbp',
                currencyDisplay: 'symbol',
                minimumFractionDigits: 0
            }
        );
    }, [intl]);

    // Build all meessage params to pass to messages
    const messageParams = useMemo(() => {
        const coverablesModel = model.lobData.privateHome.coverables;
        const {
            buildingsSelect, contentsSelect, buildingsAd, contentsAd, buildingsPlus, contentsPlus
        } = getAllCoverLevels;
        const { rebuildCost } = coverablesModel.propertyDetails;
        const { contentsTotal } = coverablesModel.propertyDetails;
        const personalBelongingsCover = getAllCoverLevels.unSpecifiedItemCover
            && SgSubmissionUtil.getTermByCode(getCoverages.belongingsAwayCover, SphConstants.COVERAGES_CODES.PersonalBelongingsCoverTotalValueTerm);
        const contentsCoverValuables = getAllCoverLevels.contentsCover
            && SgSubmissionUtil.getTermByCode(getCoverages.contentsCover, SphConstants.COVERAGES_CODES.ContentsCoverValuablesLimit);

        return {
            rebuildCostBuildings: rebuildCost && rebuildCost.amount ? getCurrencyValue(rebuildCost)
                : getCurrencyValue(SphConstants.SG_HOME_BUILDINGS_TOTAL_LIMITS),
            contentsTotal: contentsTotal ? getCurrencyValue(contentsTotal) : '',
            // eslint-disable-next-line no-nested-ternary
            buildingOrContentAccidentalDamage: buildingsSelect && contentsSelect ? `${SphConstants.IPID_PAGE_CONSTANTS.LossOrDamage}`
                // eslint-disable-next-line no-nested-ternary
                : buildingsSelect ? `${SphConstants.IPID_PAGE_CONSTANTS.Damage} ${SphConstants.IPID_PAGE_CONSTANTS.ToYour}
                 ${SphConstants.IPID_PAGE_CONSTANTS.Buildings}` : contentsSelect ? `${SphConstants.IPID_PAGE_CONSTANTS.LossOfOrDamage}
                  ${SphConstants.IPID_PAGE_CONSTANTS.ToYour} ${SphConstants.IPID_PAGE_CONSTANTS.Contents}` : '',
            // eslint-disable-next-line no-nested-ternary
            buildingOrContentADAccidentalDamage: (buildingsAd || buildingsPlus) && (contentsAd || contentsPlus)
                ? `${SphConstants.IPID_PAGE_CONSTANTS.LossOrDamage} ${SphConstants.IPID_PAGE_CONSTANTS.ToYour} 
                ${SphConstants.IPID_PAGE_CONSTANTS.Buildings} ${SphConstants.IPID_PAGE_CONSTANTS.And} ${SphConstants.IPID_PAGE_CONSTANTS.Contents}`
                // eslint-disable-next-line no-nested-ternary
                : buildingsAd || buildingsPlus ? `${SphConstants.IPID_PAGE_CONSTANTS.Damage} ${SphConstants.IPID_PAGE_CONSTANTS.ToYour} 
                ${SphConstants.IPID_PAGE_CONSTANTS.Buildings}` : contentsAd || contentsPlus ? `${SphConstants.IPID_PAGE_CONSTANTS.Damage}
                    ${SphConstants.IPID_PAGE_CONSTANTS.ToYour} ${SphConstants.IPID_PAGE_CONSTANTS.Contents}` : '',
            personalBelongingsValue: getAllCoverLevels.unSpecifiedItemCover
                ? personalBelongingsCover.chosenTermValue : getCurrencyValue(SphConstants.PERSONAL_BELONGINGS_VALUE),
            valuablesLimit: contentsCoverValuables ? getCurrencyValue(parseFloat(contentsCoverValuables.chosenTermValue.replace(',', ''))) : '',
            // eslint-disable-next-line no-nested-ternary
            premierMatchingPairSetsValue: buildingsPlus && contentsPlus ? `${SphConstants.IPID_PAGE_CONSTANTS.BUILDINGS_CONTENTS}`
                // eslint-disable-next-line no-nested-ternary
                : buildingsPlus ? `${SphConstants.IPID_PAGE_CONSTANTS.Buildings}`
                    : contentsPlus ? `${SphConstants.IPID_PAGE_CONSTANTS.Contents}` : '',
            landlorBuildingsUpToValue: rebuildCost && rebuildCost.amount
                ? getCurrencyValue(rebuildCost) : getCurrencyValue(SphConstants.LANDLORD_BUILDINGS_UP_TO),
            premierMoneyAndCreditcardValue: contentsPlus ? `${SphConstants.IPID_PAGE_CONSTANTS.PREMIER_CREDITCARD_VALUE[0]}`
                : `${SphConstants.IPID_PAGE_CONSTANTS.PREMIER_CREDITCARD_VALUE[1]}`
        };
    }, [model, getAllCoverLevels, getCoverages.belongingsAwayCover, getCoverages.contentsCover, getCurrencyValue]);

    // To check which coverlevel items to return
    const conditionsApply = (covers, item) => {
        if (item.condition) {
            return (covers[item.condition]);
        }

        if (!item.cover) {
            return true;
        }

        const coverage = covers[item.cover];
        if (coverage) {
            if (!item.coverLevel) {
                return true;
            }

            const covLevel = covers[item.coverLevel];
            if (covLevel) {
                return true;
            }
        }

        return false;
    };

    // Pull messages and title that matches the condition based on coverlevel and translate them
    const getMessages = useCallback((cover, product, type) => {
        const productConfig = IpidConfig[product];
        return SgIpidUtil.getMessages(cover, product, type, productConfig, conditionsApply, messages, messageParams, translator);
    }, [translator, messageParams]);

    // Build product title based on coverlevels and productType
    const initProductTitle = useCallback((key) => {
        let productKey = `${SphConstants.IPID_PAGE_CONSTANTS.ProductTitle}`;
        if (key === `${SphConstants.IPID_PAGE_CONSTANTS.HOME}`) {
            if (getAllCoverLevels.buildingsSelect || getAllCoverLevels.buildingsAd) {
                productKey += `${SphConstants.IPID_PAGE_CONSTANTS.BuildingsTitle}${SphConstants.COVERAGES_CODES.CoverLevelSelect}`;
            }
            if (getAllCoverLevels.contentsSelect || getAllCoverLevels.contentsAd) {
                productKey += `${SphConstants.IPID_PAGE_CONSTANTS.ContentsTitle}${SphConstants.COVERAGES_CODES.CoverLevelSelect}`;
            }
            if (getAllCoverLevels.buildingsPlus) {
                productKey += `${SphConstants.IPID_PAGE_CONSTANTS.BuildingsTitle}${SphConstants.COVERAGES_CODES.CoverLevelPlus}`;
            }
            if (getAllCoverLevels.contentsPlus) {
                productKey += `${SphConstants.IPID_PAGE_CONSTANTS.ContentsTitle}${SphConstants.COVERAGES_CODES.CoverLevelPlus}`;
            }
        } else if (key === `${SphConstants.COVERAGES_CODES.HomeEmergency}`) {
            if (getAllCoverLevels.homeHeatingEmergencyCover) {
                productKey += `${SphConstants.COVERAGES_CODES.HomeAndHeatingCover}`;
            }
        } else {
            productKey = `${SphConstants.IPID_PAGE_CONSTANTS.ProductTitle}`;
        }
        return productKey;
    }, [getAllCoverLevels.buildingsSelect, getAllCoverLevels.buildingsAd, getAllCoverLevels.contentsSelect,
        getAllCoverLevels.contentsAd, getAllCoverLevels.buildingsPlus, getAllCoverLevels.contentsPlus, getAllCoverLevels.homeHeatingEmergencyCover]);

    // Build InsuranceType description based on coverlevels and productType
    const getInsuraceTypeDesc = useCallback((key) => {
        const { coverLevel } = model.lobData.privateHome.coverables;
        const coverLevelType = stringUtil.capitalizeFirstLetter(coverLevel.replaceAll('_', ''));
        const insuranceTypeText = translator(messages[`${SphConstants.IPID_PAGE_CONSTANTS.InsuranceType}${coverLevelType}`]);
        const insuranceTypeDescCover = getCoverages.isSagaPlus
            ? translator(messages[`${SphConstants.IPID_PAGE_CONSTANTS.InsuranceTypeDescCover}${key}${SphConstants.IPID_PAGE_CONSTANTS.PlusCover}`])
            : translator(messages[`${SphConstants.IPID_PAGE_CONSTANTS.InsuranceTypeDescCover}${key}`]);
        const insuranceTypeDescription = translator(messages.InsuranceTypeDesc);
        const insuranceTypeCoverables = translator(
            messages[`${SphConstants.IPID_PAGE_CONSTANTS.InsuranceTypeDescCoverable}${key}`], { insuranceType: insuranceTypeText }
        );
        return `${insuranceTypeDescCover} ${insuranceTypeDescription} ${insuranceTypeCoverables}`;
    }, [getCoverages.isSagaPlus, model.lobData.privateHome.coverables, translator]);

    const createListFromMessages = useCallback((messageArray, params) => {
        return SgIpidUtil.createListFromMessages(messageArray, params, messages, translator);
    }, [translator]);

    // Initialise common home data
    const initCommonData = useCallback((key) => {
        const productTypeKey = stringUtil.capitalizeFirstLetter(key.replaceAll('_', ''));
        const productTypeTitle = `${SphConstants.IPID_PAGE_CONSTANTS.Title}${productTypeKey}`;
        const coverLevelTitle = `${productTypeKey}${initProductTitle(key)}`;
        let phoneNumber;
        switch (key) {
            case SphConstants.HOME:
            case SphConstants.COVERAGES_CODES.LegalExpenses: {
                phoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_IPID_HOME_PHONE_NUMBER1;
                break;
            }
            case SphConstants.LANDLORD_PRODUCT_CODE:
            case SphConstants.TAILOR_MADE_PRODUCT_CODE:
            case SphConstants.COVERAGES_CODES.HomeEmergency: {
                phoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_IPID_HOME_PHONE_NUMBER2;
                break;
            }
            default:
                phoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_IPID_HOME_PHONE_NUMBER1;
        }
        return {
            insuredList: getMessages(getAllCoverLevels, key, `${SphConstants.IPID_PAGE_CONSTANTS.Insured}`),
            notInsuredList: getMessages(getAllCoverLevels, key, `${SphConstants.IPID_PAGE_CONSTANTS.NotInsured}`),
            restrictionsList: getMessages(getAllCoverLevels, key, `${SphConstants.IPID_PAGE_CONSTANTS.Restrictions}`),
            coveredList: createListFromMessages(coverLevelMessages.commonCoveredCountries, null),
            obligationsList: [],
            header: {
                title: translator(messages[productTypeTitle]),
                suffix: key,
                product: translator(messages[coverLevelTitle]),
                insuranceTypeDesc: getInsuraceTypeDesc(productTypeKey)
            },
            date: {
                periodStartDate: SgDateUtil.toLongDate(model.baseData.periodStartDate),
                periodEndDate: SgDateUtil.addDaysToLongDate(-1, model.baseData.periodEndDate)
            },
            phoneNumber: phoneNumber
        };
    }, [getAllCoverLevels, getInsuraceTypeDesc, getMessages, initProductTitle, model, translator,
        coverLevelMessages.commonCoveredCountries, createListFromMessages]);

    const ipidPaymentMessage = useMemo(() => {
        const { jobType } = model.baseData;
        if (jobType && jobType === CommonConstants.JOB_TYPES.RENEWAL) {
            const { paymentMethodCode, paymentPlanCode, cardCAuth } = model;
            const paymentMethod = translator(paymentMethodCode === CommonConstants.PAYMENT_METHOD_CODES.DIRECT_DEBIT
                ? messages.DirectDebit : messages.DebitCretidCard);
            let paymentPlan;
            let singlePaymentWording = '';

            if (paymentPlanCode.toLowerCase().includes(CommonConstants.PAYMENT_FREQUENCY.ANNUAL)) {
                paymentPlan = paymentMethodCode === CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD && cardCAuth
                    ? translator(messages.Continuous) : CommonConstants.PAYMENT_FREQUENCY.ANNUAL;
                singlePaymentWording = translator(messages.SinglePayment);
            }
            if (paymentPlanCode.toLowerCase().includes(CommonConstants.PAYMENT_FREQUENCY.MONTHLY)) {
                paymentPlan = CommonConstants.PAYMENT_FREQUENCY.MONTHLY;
            }
            if (paymentPlanCode.toLowerCase().includes(CommonConstants.PAYMENT_FREQUENCY.QUARTERLY)) {
                paymentPlan = CommonConstants.PAYMENT_FREQUENCY.QUARTERLY;
            }

            if ((paymentMethodCode === CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD
                || paymentMethodCode === CommonConstants.PAYMENT_METHOD_CODES.CHEQUE)
                && paymentPlan !== CommonConstants.PAYMENT_FREQUENCY.QUARTERLY && !cardCAuth) {
                return translator(messages.HowDoIPayRenewalCreditCardOrCheque);
            }

            return translator(messages.HowDoIPayRenewal, {
                singlePayment: singlePaymentWording,
                paymentPlan: paymentPlan,
                paymentMethod: paymentMethod
            });
        }
        return translator(messages.HowDoIPay);
    }, [model, translator]);

    const initIpidData = useMemo(() => {
        const data = initCommonData(productType);
        data.howDoIPay = ipidPaymentMessage;
        data.endDateMessage = '';
        if (productType === SphConstants.IPID_PAGE_CONSTANTS.HOME && getAllCoverLevels.contentsCover) {
            data.coveredList.push(translator(messages.BicycleCover));
        }
        if (productType === SphConstants.IPID_PAGE_CONSTANTS.Tailormade && getAllCoverLevels.contentsCover) {
            data.coveredList.push(translator(messages.TailormadeCoveredCountries));
        }
        if (getAllCoverLevels.unSpecifiedItemCover || getAllCoverLevels.contentsPlus || getAllCoverLevels.specifiedItemsCoverAnywhere) {
            data.coveredList.push(translator(messages.SPHPersonalBelongingsUnspecifiedItems, {
                // eslint-disable-next-line no-nested-ternary
                unspecifiedAndSpecified: (getAllCoverLevels.unSpecifiedItemCover && getAllCoverLevels.specifiedItemsCoverAnywhere)
                || (getAllCoverLevels.specifiedItemsCoverAnywhere && getAllCoverLevels.contentsPlus)
                    ? SphConstants.IPID_PAGE_CONSTANTS.UnspecifiedandSpecified
                    // eslint-disable-next-line no-nested-ternary
                    : getAllCoverLevels.unSpecifiedItemCover || getAllCoverLevels.contentsPlus ? SphConstants.IPID_PAGE_CONSTANTS.Unspecified
                        : SphConstants.IPID_PAGE_CONSTANTS.Specified
            }));
        }
        data.obligationsList = createListFromMessages(coverLevelMessages.commonObligations, null);
        return data;
    }, [initCommonData, productType, translator, getAllCoverLevels, coverLevelMessages.commonObligations,
        createListFromMessages, ipidPaymentMessage]);

    const initIpidAddonData = useCallback((key) => {
        const data = initCommonData(key);
        data.howDoIPay = translator(messages.HowDoIPayOther);
        data.endDateMessage = translator(messages.EndDateMessage);
        data.hideCloseButton = true;
        return data;
    }, [initCommonData, translator]);

    const initIpidSheData = useMemo(() => {
        const data = initIpidAddonData(SphConstants.COVERAGES_CODES.HomeEmergency);
        data.isSHECover = true;
        data.obligationsList = createListFromMessages(coverLevelMessages.sheObligations, null);
        if (!getAllCoverLevels.homeHeatingEmergencyCover) {
            data.notInsuredList[0].keys.push(translator(messages.SheBoilerOrHeatingProblems));
        }
        return data;
    }, [initIpidAddonData, createListFromMessages, coverLevelMessages.sheObligations, isRenewal, getAllCoverLevels, translator]);

    const initIpidLegalData = useMemo(() => {
        const data = initIpidAddonData(SphConstants.COVERAGES_CODES.LegalExpenses);
        if (data) {
            data.coveredList.push(translator(messages.SPHLegalExpensesCovered));
        }
        data.obligationsList = createListFromMessages(coverLevelMessages.legalObligations, null);
        return data;
    }, [initIpidAddonData, createListFromMessages, translator, coverLevelMessages.legalObligations]);

    const overrides = {

        homeIpid: {
            data: initIpidData
        },
        homeIpidLegal: {
            visible: getCoverages.legalCover !== undefined && getCoverages.legalCover.selected,
            data: initIpidLegalData

        },
        homeIpidShe: {
            visible: getCoverages.sheEmergencyCover !== undefined && getCoverages.sheEmergencyCover.selected,
            data: initIpidSheData

        }
    };

    const resolvers = {
        resolveComponentMap: {
            SgIpidDetails: SgIpidDetails
        },
    };

    return renderContentFromMetadata(metadata.componentContent, overrides, resolvers);
}

export default SgSphIpid;
